import { SpanData, SpanTag, SpanLog } from './types';
import { generateShortId, getTimestamp, stringifyValue } from './utils';

/**
 * Span 类 - 表示一个操作单元
 */
export class Span {
  private _spanId: string;
  private _parentSpanId?: string;
  private _operationName: string;
  private _startTime: number;
  private _endTime?: number;
  private _tags: Map<string, string> = new Map();
  private _logs: SpanLog[] = [];
  private _isError: boolean = false;
  private _finished: boolean = false;

  constructor(operationName: string, parentSpanId?: string) {
    this._spanId = generateShortId();
    this._parentSpanId = parentSpanId;
    this._operationName = operationName;
    this._startTime = getTimestamp();
  }

  /**
   * 获取 Span ID
   */
  get spanId(): string {
    return this._spanId;
  }

  /**
   * 获取父 Span ID
   */
  get parentSpanId(): string | undefined {
    return this._parentSpanId;
  }

  /**
   * 获取操作名称
   */
  get operationName(): string {
    return this._operationName;
  }

  /**
   * 获取开始时间
   */
  get startTime(): number {
    return this._startTime;
  }

  /**
   * 获取结束时间
   */
  get endTime(): number | undefined {
    return this._endTime;
  }

  /**
   * 获取持续时间（毫秒）
   */
  get duration(): number | undefined {
    if (this._endTime) {
      return this._endTime - this._startTime;
    }
    return undefined;
  }

  /**
   * 是否已结束
   */
  get finished(): boolean {
    return this._finished;
  }

  /**
   * 是否标记为错误
   */
  get isError(): boolean {
    return this._isError;
  }

  /**
   * 添加标签
   */
  addTag(key: string, value: string): this;
  addTag(tags: Record<string, any>): this;
  addTag(keyOrTags: string | Record<string, any>, value?: string): this {
    if (this._finished) {
      console.warn(`Cannot add tag to finished span: ${this._operationName}`);
      return this;
    }
    
    // 如果第一个参数是对象，则批量添加标签
    if (typeof keyOrTags === 'object' && keyOrTags !== null) {
      Object.entries(keyOrTags).forEach(([key, val]) => {
        this._tags.set(key, stringifyValue(val));
      });
    } else if (typeof keyOrTags === 'string' && value !== undefined) {
      // 如果第一个参数是字符串，第二个参数是值，则添加单个标签
      this._tags.set(keyOrTags, stringifyValue(value));
    }
    
    return this;
  }

  /**
   * 获取标签
   */
  getTag(key: string): string | undefined {
    return this._tags.get(key);
  }

  /**
   * 获取所有标签
   */
  getTags(): SpanTag[] {
    return Array.from(this._tags.entries()).map(([key, value]) => ({
      key,
      value,
    }));
  }

  /**
   * 添加日志
   * 注意：timestamp 字段会自动生成，无需手动设置
   */
  addLog(logData: Record<string, any>): this {
    if (this._finished) {
      console.warn(`Cannot add log to finished span: ${this._operationName}`);
      return this;
    }

    // 过滤掉自动生成的字段，避免用户手动设置
    const filteredData = { ...logData };
    delete filteredData.timestamp; // timestamp 自动生成
    delete filteredData.time; // 兼容其他可能的命名

    const log: SpanLog = {
      timestamp: getTimestamp(),
      fields: Object.entries(filteredData).map(([key, value]: [string, any]) => ({
        key,
        value: stringifyValue(value),
      })),
    };
    this._logs.push(log);
    return this;
  }

  /**
   * 获取所有日志
   */
  getLogs(): SpanLog[] {
    return [...this._logs];
  }

  /**
   * 标记为错误
   */
  setError(error: boolean = true): this {
    if (this._finished) {
      console.warn(`Cannot set error on finished span: ${this._operationName}`);
      return this;
    }
    this._isError = error;
    return this;
  }

  /**
   * 结束 Span
   */
  finish(): this {
    if (this._finished) {
      console.warn(`Span already finished: ${this._operationName}`);
      return this;
    }
    this._endTime = getTimestamp();
    this._finished = true;
    return this;
  }

  /**
   * 转换为 SpanData
   */
  toData(): SpanData {
    return {
      spanId: this._spanId,
      parentSpanId: this._parentSpanId,
      operationName: this._operationName,
      startTime: this._startTime,
      endTime: this._endTime,
      duration: this.duration,
      tags: this.getTags(),
      logs: this.getLogs(),
      isError: this._isError,
    };
  }
}

